import { formatBillingDate } from "../billingDates.utils";
import { invoiceStatusLabel } from "../ui/invoiceStatus.utils";

/** Most rows one Export asks for (BILL-018, the server's page limit). */
export const EXPORT_LIMIT = 5000;

/** Invoice / payment labels without a name aren't offered (BILL-010). */
export const withNamedLabels = <T extends { name?: string | null }>(
  labels: T[] | null | undefined,
): T[] => (labels || []).filter((label) => !!label?.name?.trim());

type ExportInvoice = {
  customerName?: string | null;
  invoiceNumber?: string | number | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  total?: number | null;
  balance?: number | null;
  status?: string | null;
  userId?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  label?: string | null;
};

/**
 * Export rows (QA batch 17): the same words and dates as the list ("Overdue",
 * "Partially paid", "Oct 22, 2026"; BILL-006 / UI-005).
 */
export const invoiceExportRows = (invoices: ExportInvoice[]) =>
  invoices.map((invoice) => ({
    Contact: invoice?.customerName || "",
    "Invoice No.": invoice?.invoiceNumber ?? "",
    "Invoice Date": formatBillingDate(invoice?.invoiceDate),
    "Due Date": formatBillingDate(invoice?.dueDate),
    Total: invoice?.total || 0,
    Balance: invoice?.balance || 0,
    Status: invoiceStatusLabel(invoice?.status),
    Assignee: invoice?.userId
      ? `${invoice?.userFirstName || ""} ${invoice?.userLastName || ""}`.trim()
      : "",
    Label: invoice?.label || "",
  }));

export const OUTSTANDING_HINT =
  "What is still owed on this year's invoices (dated 1 Jan up to today): every balance above $0. Paid, $0 and overpaid invoices count as 0. The list's \"Total (all listed)\" adds every listed invoice as it is, including negative (overpaid) balances, and its date filter uses your computer's calendar day.";

/**
 * Card hint that names the overpaid invoices (DATA-003, QA batch 17): they are
 * the usual reason the card is higher than the list's balance total.
 */
export const outstandingHint = (
  overpaid: { invoiceNo: number; balance: number }[] | null | undefined,
) => {
  if (!overpaid?.length) return OUTSTANDING_HINT;
  const money = (value: number) =>
    `-$${Math.abs(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  const total = overpaid.reduce((sum, item) => sum + item.balance, 0);
  const listed = overpaid
    .slice(0, 5)
    .map((item) => `#${item.invoiceNo} (${money(item.balance)})`)
    .join(", ");
  const more = overpaid.length > 5 ? ` and ${overpaid.length - 5} more` : "";
  return `${OUTSTANDING_HINT} This year's overpaid invoices, counted as $0 here: ${listed}${more} — together ${money(
    total,
  )}, so the list's total is lower by that much.`;
};
